import { Address } from './address';

export enum BillingProfileType {
  Private = 'private',
  Professional = 'professional'
}

export interface BillingProfileContactInfo {
  firstName?: string;
  lastName?: string;
  companyName?: string;
  vatNumber?: string;
  email?: string;
}

export interface BillingProfileBankingInfo {
  iban: string;
  bankName?: string;
  accountHolder: string;
  bic?: string; // Bank Identifier Code (optional)
}

/**
 * #633 — which reference the tenants of this profile's leases are asked for:
 * Proporio's structured communication (default), free text, or the landlord's
 * own, typed lease by lease.
 */
export type PaymentReferenceMode = 'proporio' | 'free-text' | 'custom';

export class BillingProfile {
  profileId!: string;
  profileName!: string; // User-friendly name like "Personal" or "Company ABC"
  profileType!: BillingProfileType;
  contactInfo!: BillingProfileContactInfo;
  address!: Address;
  bankingInfo!: BillingProfileBankingInfo;
  paymentReferenceMode?: PaymentReferenceMode;
  isDefault!: boolean;
  ownerUID!: string;
  createdAt!: Date;
  updatedAt!: Date;

  constructor(data: Partial<BillingProfile> = {}) {
    if (data.profileId) this.profileId = data.profileId;
    this.profileName = data.profileName || '';
    this.profileType = data.profileType || BillingProfileType.Private;
    this.contactInfo = data.contactInfo || {};
    this.address = data.address ? new Address(data.address) : new Address();
    this.bankingInfo = data.bankingInfo || { iban: '', accountHolder: '' };
    this.paymentReferenceMode =
      data.paymentReferenceMode === 'free-text' || data.paymentReferenceMode === 'custom'
        ? data.paymentReferenceMode
        : 'proporio';
    this.isDefault = data.isDefault || false;
    this.ownerUID = data.ownerUID || '';
    this.createdAt = data.createdAt || new Date();
    this.updatedAt = data.updatedAt || new Date();
  }

  /**
   * Get the display name for this billing profile
   */
  get displayName(): string {
    if (this.profileType === BillingProfileType.Professional && this.contactInfo.companyName) {
      return this.contactInfo.companyName;
    }
    
    if (this.contactInfo.firstName || this.contactInfo.lastName) {
      return `${this.contactInfo.firstName || ''} ${this.contactInfo.lastName || ''}`.trim();
    }
    
    return this.profileName;
  }

  /**
   * Get the formatted address as a single string
   */
  get formattedAddress(): string {
    const parts = [
      this.address.line1,
      this.address.line2,
      this.address.zip && this.address.city ? `${this.address.zip} ${this.address.city}` : this.address.city,
      this.address.country
    ].filter(Boolean);
    
    return parts.join(', ');
  }

  /**
   * Validate the billing profile data
   */
  isValid(): boolean {
    // Basic validation
    if (!this.profileName || !this.profileType || !this.ownerUID) {
      return false;
    }

    // Contact info validation
    if (this.profileType === BillingProfileType.Private) {
      if (!this.contactInfo.firstName && !this.contactInfo.lastName) {
        return false;
      }
    } else if (this.profileType === BillingProfileType.Professional) {
      if (!this.contactInfo.companyName) {
        return false;
      }
    }

    // Banking info validation
    if (!this.bankingInfo.iban || !this.bankingInfo.accountHolder) {
      return false;
    }

    // Address validation (at least city and country)
    if (!this.address.city || !this.address.country) {
      return false;
    }

    return true;
  }

  /**
   * Get a summary of the billing profile for display purposes
   */
  getSummary(): string {
    const name = this.displayName;
    const type = this.profileType === BillingProfileType.Private ? 'Private' : 'Professional';
    const city = this.address.city;
    return `${name} (${type}) - ${city}`;
  }

  /**
   * Get the owner/landlord details formatted for contract documents
   */
  getOwnerDetails(): {
    name: string;
    address: string;
    iban: string;
    vatNumber?: string;
    companyName?: string;
  } {
    return {
      name: this.displayName,
      address: this.formattedAddress,
      iban: this.bankingInfo.iban,
      vatNumber: this.contactInfo.vatNumber,
      companyName: this.contactInfo.companyName
    };
  }
} 